import dayjs from 'dayjs';
import { ALLOWED_RFQ_ATTACHMENT_MIME, MAX_RFQ_ATTACHMENT_BYTES } from '../constants/rfqManagement';
import { readFileAsBase64 } from './vendorEntity';
import { getStoreActorId } from '../hooks/useStoreScope';
import { generateTxnNumber } from '../utils';

export function normalizeApiList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export function toFormDate(isoOrDate) {
  if (!isoOrDate) return '';
  return String(isoOrDate).includes('T')
    ? dayjs(isoOrDate).format('YYYY-MM-DD')
    : isoOrDate;
}

/** Value for `<input type="datetime-local">` (`YYYY-MM-DDTHH:mm`). */
export function toFormDateTime(isoOrDate) {
  if (!isoOrDate) return '';
  const d = dayjs(isoOrDate);
  return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '';
}

export function toApiDateTime(value) {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.toISOString() : value;
}

/** Backend `issue_date` is a Date (no time). */
export function toApiIssueDate(value) {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
}

const DEFAULT_MINIMUM_QUOTES = 3;

/** Backend requires >= 1; API default is 3. */
export function toMinimumRequiredQuotes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return DEFAULT_MINIMUM_QUOTES;
  return Math.floor(n);
}

export function pickLastPurchaseRate(source) {
  if (!source || typeof source !== 'object') return null;
  const keys = [
    'last_purchase_rate',
    'last_purchase_price',
    'last_rate',
    'purchase_rate',
    'default_purchase_rate',
    'unit_rate',
    'lastPurchase',
    'wac',
    'moving_average_cost',
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const raw = source[keys[i]];
    if (raw === '' || raw == null) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

export function emptyRfqLine() {
  return {
    item_id: '',
    item_name: '',
    item_code: '',
    hsn: '',
    specification: '',
    required_quantity: '',
    uom_id: '',
    uom_code: '',
    required_by_date: '',
    remarks: '',
    last_rate: null,
    gst_rate: '',
  };
}

export function emptyRfqAttachment() {
  return {
    file_name: '',
    mime_type: '',
    content_base64: '',
  };
}

/** API RFQResponse → UI-friendly shape */
export function fromApiRfq(r = {}) {
  const payload = r.rfq_payload && typeof r.rfq_payload === 'object' ? r.rfq_payload : {};
  return {
    id: r.id || r.rfq_id,
    rfq_id: r.id || r.rfq_id,
    rfq_number: r.rfq_number ?? '',
    title: r.rfq_title ?? r.title ?? '',
    rfq_title: r.rfq_title ?? r.title ?? '',
    rfq_type: r.rfq_type ?? 'STANDARD',
    status: r.status ?? 'DRAFT',
    issue_date: r.issue_date ?? '',
    deadline: r.submission_deadline ?? r.deadline ?? '',
    submission_deadline: r.submission_deadline ?? r.deadline ?? '',
    required_quotes_count: r.minimum_required_quotes ?? r.required_quotes_count ?? '',
    minimum_required_quotes: r.minimum_required_quotes ?? r.required_quotes_count ?? '',
    is_open_bid: Boolean(r.is_open_bid) || Boolean(payload.is_open_bid)
      || Boolean(r.allow_unregistered) || Boolean(payload.allow_unregistered),
    is_sealed_bid: Boolean(r.is_sealed_bid) || Boolean(payload.is_sealed_bid),
    unsealed_at: r.unsealed_at ?? null,
    unseal_reason: r.unseal_reason ?? '',
    terms_and_conditions: r.terms_and_conditions ?? '',
    notes: r.notes ?? '',
    department_id: r.department_id ?? '',
    department_name: r.department_name || payload.department_name || '',
    requested_by: r.requested_by ?? '',
    requested_by_name: r.requested_by_name || payload.requested_by_name || r.buyer_name || '',
    publish_at: r.publish_at ?? null,
    invited_vendor_ids: payload.invited_vendor_ids ?? r.vendor_ids ?? [],
    attachments: payload.attachments ?? {},
    rfq_payload: payload,
    is_active: r.is_active ?? true,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

/** API RFQItemResponse → UI line */
export function fromApiRfqItem(item = {}) {
  return {
    id: item.id,
    item_id: item.item_id,
    item_name: item.item_name
      || item.item?.item_name
      || item.item_payload?.item_name
      || '',
    item_code: item.item_code || item.item_payload?.item_code || '',
    hsn: item.hsn_code || item.hsn || item.item?.hsn_code || item.item_payload?.hsn || '',
    required_quantity: item.requested_quantity ?? item.required_quantity ?? '',
    specification: item.specification ?? '',
    uom_id: item.uom_id ?? '',
    uom_code: item.uom_code ?? item.uom_name ?? item.item_payload?.uom_code ?? '',
    required_by_date: item.required_by_date ?? '',
    remarks: item.remarks ?? '',
    last_rate: pickLastPurchaseRate(item)
      ?? pickLastPurchaseRate(item.item_payload)
      ?? pickLastPurchaseRate(item.item)
      ?? null,
    gst_rate: item.gst_rate
      ?? item.tax_percent
      ?? item.item?.gst_rate
      ?? item.item_payload?.gst_rate
      ?? '',
  };
}

/**
 * Row indexes that repeat an item already chosen further up the list.
 *
 * The same item twice would create two RFQ lines a vendor cannot tell apart,
 * and two competing prices for one requirement. Used by both the line editor
 * (to mark the row) and the form (to block saving), so it lives here rather
 * than being written twice.
 */
export function findDuplicateItemRows(lines = []) {
  const firstSeen = new Map();
  const duplicates = new Set();
  lines.forEach((line, i) => {
    if (!line?.item_id) return;
    if (firstSeen.has(line.item_id)) duplicates.add(i);
    else firstSeen.set(line.item_id, i);
  });
  return duplicates;
}

export function toSubmissionDeadline(dateStr) {
  if (!dateStr) return undefined;
  return toApiDateTime(dateStr);
}

export function validateRfqAttachment(file) {
  if (!file) return 'No file selected';
  if (!ALLOWED_RFQ_ATTACHMENT_MIME.includes(file.type)) {
    return 'Allowed formats: PDF, JPG, PNG, DOC, DOCX';
  }
  if (file.size > MAX_RFQ_ATTACHMENT_BYTES) return 'File must be 5 MB or smaller';
  return null;
}

export async function fileToRfqAttachment(file) {
  const err = validateRfqAttachment(file);
  if (err) throw new Error(err);
  const content_base64 = await readFileAsBase64(file);
  return {
    file_name: file.name,
    mime_type: file.type,
    content_base64,
  };
}

export function attachmentsToPayload(rows) {
  const out = {};
  (rows || []).forEach((row, i) => {
    if (!row?.file_name || !row?.content_base64) return;
    const key = `ATTACHMENT_${i + 1}`;
    out[key] = {
      file_name: row.file_name,
      mime_type: row.mime_type,
      content_base64: row.content_base64,
    };
  });
  return out;
}

/** POST /rfqs body — spec #16 */
export function snapshotPortalItems(lines = []) {
  return (lines || [])
    .filter((line) => line?.item_id)
    .map((line) => ({
      item_id: line.item_id,
      rfq_item_id: line.id || line.rfq_item_id || undefined,
      item_name: line.item_name || '',
      item_code: line.item_code || '',
      hsn: line.hsn || '',
      uom: line.uom_code || '',
      specification: line.specification || '',
      requested_quantity: line.required_quantity,
      last_rate: line.last_rate != null ? line.last_rate : undefined,
      ...(line.gst_rate !== '' && line.gst_rate != null
        ? { gst_rate: line.gst_rate, tax_percent: line.gst_rate }
        : {}),
    }));
}

function toItemPayload(line = {}) {
  return {
    item_name: line.item_name || undefined,
    item_code: line.item_code || undefined,
    hsn: line.hsn || undefined,
    uom_code: line.uom_code || undefined,
    last_rate: line.last_rate != null ? line.last_rate : undefined,
    gst_rate: line.gst_rate !== '' && line.gst_rate != null ? line.gst_rate : undefined,
    tax_percent: line.gst_rate !== '' && line.gst_rate != null ? line.gst_rate : undefined,
  };
}

export function toRfqCreatePayload(data, { vendorIds = [], attachments = [], lines = [] } = {}) {
  const actor = getStoreActorId();
  const attachmentMap = attachmentsToPayload(attachments);
  const portal_items = snapshotPortalItems(lines);
  const rfq_payload = {
    ...(attachmentMap && Object.keys(attachmentMap).length ? { attachments: attachmentMap } : {}),
    ...(vendorIds?.length ? { invited_vendor_ids: vendorIds } : {}),
    is_open_bid: Boolean(data.is_open_bid),
    is_sealed_bid: Boolean(data.is_sealed_bid),
    allow_unregistered: Boolean(data.is_open_bid),
    invite_only: !Boolean(data.is_open_bid),
    who_can_bid: Boolean(data.is_open_bid) ? 'OPEN' : 'INVITED',
    ...(portal_items.length ? { portal_items } : {}),
  };

  return {
    rfq_number: data.rfq_number?.trim(),
    rfq_title: data.rfq_title?.trim() || data.title?.trim(),
    rfq_type: data.rfq_type || 'STANDARD',
    requested_by: actor || undefined,
    issue_date: toApiIssueDate(data.issue_date) || dayjs().format('YYYY-MM-DD'),
    submission_deadline: toSubmissionDeadline(data.submission_deadline || data.deadline),
    minimum_required_quotes: toMinimumRequiredQuotes(
      data.minimum_required_quotes ?? data.required_quotes_count,
    ),
    is_open_bid: Boolean(data.is_open_bid),
    is_sealed_bid: Boolean(data.is_sealed_bid),
    allow_unregistered: Boolean(data.is_open_bid),
    terms_and_conditions: data.terms_and_conditions?.trim() || undefined,
    notes: data.notes?.trim() || undefined,
    rfq_payload: Object.keys(rfq_payload).length ? rfq_payload : {},
  };
}

/** PATCH /rfqs/{id} body — spec #19 */
export function toRfqUpdatePayload(data, {
  vendorIds = [], attachments = [], existingPayload = {}, lines = [],
} = {}) {
  const attachmentMap = attachmentsToPayload(attachments);
  const portal_items = snapshotPortalItems(lines);
  const rfq_payload = {
    ...existingPayload,
    ...(attachmentMap && Object.keys(attachmentMap).length ? { attachments: attachmentMap } : {}),
    invited_vendor_ids: vendorIds ?? existingPayload.invited_vendor_ids ?? [],
    is_open_bid: Boolean(data.is_open_bid),
    is_sealed_bid: Boolean(data.is_sealed_bid),
    allow_unregistered: Boolean(data.is_open_bid),
    invite_only: !Boolean(data.is_open_bid),
    who_can_bid: Boolean(data.is_open_bid) ? 'OPEN' : 'INVITED',
    ...(portal_items.length ? { portal_items } : {}),
  };

  return {
    rfq_title: data.rfq_title?.trim() || data.title?.trim(),
    rfq_type: data.rfq_type || 'STANDARD',
    issue_date: toApiIssueDate(data.issue_date) || undefined,
    submission_deadline: toSubmissionDeadline(data.submission_deadline || data.deadline),
    minimum_required_quotes: toMinimumRequiredQuotes(
      data.minimum_required_quotes ?? data.required_quotes_count,
    ),
    is_open_bid: Boolean(data.is_open_bid),
    is_sealed_bid: Boolean(data.is_sealed_bid),
    allow_unregistered: Boolean(data.is_open_bid),
    terms_and_conditions: data.terms_and_conditions?.trim() || undefined,
    notes: data.notes?.trim() || undefined,
    rfq_payload,
  };
}

/** PATCH /rfq-items/{id} body */
export function toRfqItemUpdateBody(line) {
  const qty = line.required_quantity;
  return {
    requested_quantity: qty !== '' && qty != null ? String(qty) : undefined,
    uom_id: line.uom_id || undefined,
    required_by_date: line.required_by_date || undefined,
    specification: line.specification?.trim() || undefined,
    remarks: line.remarks?.trim() || undefined,
    item_payload: toItemPayload(line),
  };
}

export function lineNeedsUpdate(original, edited) {
  return String(original.required_quantity) !== String(edited.required_quantity)
    || (original.uom_id || '') !== (edited.uom_id || '')
    || (original.required_by_date || '') !== (edited.required_by_date || '')
    || (original.specification || '').trim() !== (edited.specification || '').trim()
    || (original.remarks || '').trim() !== (edited.remarks || '').trim()
    || (original.item_name || '') !== (edited.item_name || '');
}

/**
 * POST /vendor-quotes/{id}/evaluate body.
 *
 * Weights come in as percentages summing to 100 (how the UI shows them) and go
 * out as fractions, which is what the service expects. Sending them matters:
 * omitted, the service silently falls back to its own 40/60 default and the
 * stored total_score will not match what the screen showed.
 */
export function toEvaluateQuoteBody({
  technical_score, commercial_score, evaluation_status, remarks,
  technical_weight_pct = 40, commercial_weight_pct = 60,
}) {
  return {
    technical_score: String(technical_score),
    commercial_score: String(commercial_score),
    technical_weight: String(technical_weight_pct / 100),
    commercial_weight: String(commercial_weight_pct / 100),
    evaluation_status,
    remarks: remarks?.trim() || undefined,
  };
}

/**
 * POST /vendor-quotes/{id}/accept body.
 *
 * `awarded_rfq_item_ids` turns this into a split award: the quote wins only
 * those items and the rest of the RFQ stays open for other vendors. Omit it for
 * a full award. A split needs a distinct acceptance_number per vendor, so the
 * caller can pass one in rather than relying on the random generator.
 */
export function toAcceptQuoteBody({
  remarks, reject_others = true, awarded_rfq_item_ids, line_awards, acceptance_number,
} = {}) {
  const actor = getStoreActorId();
  const awards = (line_awards || []).filter((a) => Number(a.awarded_quantity) > 0);
  const itemIds = awarded_rfq_item_ids?.length
    ? awarded_rfq_item_ids
    : awards.map((a) => a.rfq_item_id);
  return {
    acceptance_number: acceptance_number || generateTxnNumber('ACC'),
    accepted_by: actor || undefined,
    valid_from: dayjs().format('YYYY-MM-DD'),
    valid_until: dayjs().add(6, 'month').format('YYYY-MM-DD'),
    remarks: remarks?.trim() || undefined,
    reject_others,
    ...(itemIds?.length ? { awarded_rfq_item_ids: itemIds } : {}),
    ...(awards.length ? {
      line_awards: awards.map((a) => ({
        rfq_item_id: a.rfq_item_id,
        awarded_quantity: String(a.awarded_quantity),
      })),
    } : {}),
  };
}

/** POST /purchase-orders/from-accepted-quote body */
export function toPoFromQuoteBody(acceptedQuoteId, { po_number, po_date, expected_delivery_date, remarks } = {}) {
  return {
    po_number: po_number || generateTxnNumber('PO'),
    accepted_quote_id: acceptedQuoteId,
    po_date: po_date || dayjs().format('YYYY-MM-DD'),
    expected_delivery_date: expected_delivery_date || dayjs().add(30, 'day').format('YYYY-MM-DD'),
    priority: 'NORMAL',
    remarks: remarks?.trim() || undefined,
  };
}

export function quoteLifecycleStatus(quote) {
  if (!quote || typeof quote !== 'object') return '';
  const row = Array.isArray(quote) ? quote[0] : (quote.vendor_quote || quote.quote || quote);
  if (!row || typeof row !== 'object') return '';
  const raw = String(row.status || row.quote_status || '').toUpperCase();
  if (raw === 'SUCCESS' || raw === 'FAILED' || raw === 'OK') {
    return String(row.quote_status || row.lifecycle_status || row.state || '').toUpperCase();
  }
  return raw;
}

export function canAcceptQuote(quote) {
  if (!quote) return false;
  const status = quoteLifecycleStatus(quote);
  if (['DRAFT', 'WITHDRAWN', 'REJECTED', 'ACCEPTED'].includes(status)) return false;
  return Boolean(quote.id || quote.vendor_quote_id);
}

/** Backend accept still requires EVALUATED; staff UI no longer scores quotes. */
export function quoteNeedsEvaluateBeforeAccept(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'FAILED' || s === 'OK') return true;
  return !['EVALUATED', 'ACCEPTED', 'REJECTED', 'WITHDRAWN'].includes(s);
}

export function isQuoteAlreadyAcceptedMessage(message) {
  const m = String(message || '').toLowerCase();
  return /current status:\s*accepted/.test(m);
}

export function purchaseOrderId(po) {
  return po?.purchase_order_id || po?.id || '';
}

/** Whether a PO was created from this RFQ's award (full or split). */
export function purchaseOrderLinkedToRfq(po, {
  rfqId, rfqNumber, acceptedQuoteIds = [], quoteNumbers = [],
} = {}) {
  if (!po) return false;
  const accepted = po.accepted_quote_id || po.accepted_quote || po.source_accepted_quote_id;
  if (accepted && acceptedQuoteIds.includes(accepted)) return true;
  const rid = po.rfq_id || po.source_rfq_id || po.rfq;
  if (rfqId && rid === rfqId) return true;
  const haystack = [
    po.rfq_number, po.quotation_number, po.quote_no, po.remarks, po.source_document, po.notes,
  ].filter(Boolean).join(' ');
  if (rfqNumber && haystack.includes(rfqNumber)) return true;
  const qn = po.quotation_number || po.quote_no;
  if (qn && quoteNumbers.includes(qn)) return true;
  return false;
}

export function uniquePurchaseOrders(list = []) {
  const seen = new Set();
  const out = [];
  list.forEach((po) => {
    const id = purchaseOrderId(po);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(po);
  });
  return out;
}

/**
 * POs the procurement list returns have no rfq_id. Match accepted-quote ids
 * first, then vendor + awarded value (what from-accepted-quote actually stores).
 */
export function collectAwardPurchaseOrders({
  purchaseOrders = [],
  acceptedQuotes = [],
  awardedVendorIds = [],
  awardedQuoteIds = [],
  awardedAmounts = [],
  rfqId,
  rfqNumber,
  quoteNumbers = [],
} = {}) {
  const acceptedIds = acceptedQuotes.map((row) => row.id || row.accepted_quote_id).filter(Boolean);
  const poIdsFromAccepted = acceptedQuotes
    .map((row) => row.purchase_order_id || row.po_id || row.converted_po_id)
    .filter(Boolean);
  const vendors = new Set(awardedVendorIds.filter(Boolean));
  const quoteIds = new Set(awardedQuoteIds.filter(Boolean));
  const amounts = awardedAmounts.map(Number).filter((n) => Number.isFinite(n) && n > 0);

  return uniquePurchaseOrders(purchaseOrders).filter((po) => {
    if (poIdsFromAccepted.includes(purchaseOrderId(po))) return true;
    if (purchaseOrderLinkedToRfq(po, {
      rfqId, rfqNumber, acceptedQuoteIds: acceptedIds, quoteNumbers,
    })) return true;
    const source = po.source_id || po.source_reference_id || po.reference_id
      || po.vendor_quote_id || po.quote_id;
    if (source && quoteIds.has(source)) return true;
    const vid = po.vendor_id || po.vendorId;
    if (!vid || !vendors.has(vid) || !amounts.length) return false;
    const amt = Number(
      po.grand_total ?? po.total_amount ?? po.total_net_amount ?? po.net_amount
      ?? po.total_value ?? po.total_basic_amount ?? po.po_amount ?? po.amount,
    );
    return amounts.some((a) => Math.abs(a - amt) < 1);
  });
}

export function validateRfqDates(issueDate, submissionDeadline) {
  if (!issueDate || !submissionDeadline) return null;
  if (dayjs(submissionDeadline).isBefore(dayjs(issueDate))) {
    return 'Submission deadline must be on or after issue date.';
  }
  if (dayjs(submissionDeadline).isBefore(dayjs(), 'minute')) {
    return 'Submission deadline cannot be in the past.';
  }
  return null;
}

/** POST /rfqs/{id}/items body — spec #20 */
export function toRfqApiItemBody(line, submissionDeadline) {
  const qty = line.required_quantity;
  return {
    item_id: line.item_id,
    requested_quantity: qty !== '' && qty != null ? String(qty) : undefined,
    uom_id: line.uom_id || undefined,
    required_by_date: line.required_by_date
      || (submissionDeadline ? dayjs(submissionDeadline).format('YYYY-MM-DD') : undefined),
    specification: line.specification?.trim() || undefined,
    remarks: line.remarks?.trim() || undefined,
    item_name: line.item_name || undefined,
    item_payload: toItemPayload(line),
  };
}

export function getRfqWorkflowIndex(status) {
  const order = ['DRAFT', 'PUBLISHED', 'CLOSED', 'AWARDED'];
  const normalized = status === 'OPEN' ? 'PUBLISHED'
    : status === 'EVALUATED' ? 'CLOSED'
    : status;
  const idx = order.indexOf(normalized);
  return idx >= 0 ? idx : 0;
}

export function isRfqPublished(status) {
  return status === 'PUBLISHED' || status === 'OPEN';
}

export function vendorDisplayLabel(v) {
  const name = v.vendor_name || v.name || 'Vendor';
  const code = v.vendor_code ? ` (${v.vendor_code})` : '';
  return `${name}${code}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function humanVendorName(value) {
  const text = String(value || '').trim();
  if (!text || UUID_RE.test(text)) return '';
  return text;
}

/** Staff quote chip: backend still uses UNDER_EVALUATION after close; we do not score. */
export function staffQuoteChip(status) {
  const raw = String(status || '').toUpperCase();
  if (raw === 'UNDER_EVALUATION' || raw === 'EVALUATED') {
    return { status: 'SUBMITTED', label: 'Submitted' };
  }
  if (raw === 'ACCEPTED') {
    return { status: 'AWARDED', label: 'Awarded' };
  }
  return { status: raw, label: undefined };
}

/** Name from the vendor master, invitation, or quote — never a raw UUID if a name exists. */
export function resolveRfqVendorLabel(vendorId, {
  vendor, invitation, quote,
} = {}) {
  if (vendor) return vendorDisplayLabel(vendor);
  const named = [
    quote?.vendor_name,
    quote?.vendorName,
    quote?.legal_name,
    invitation?.vendor_name,
    invitation?.vendorName,
    invitation?.business_name,
    invitation?.legal_name,
  ].map(humanVendorName).find(Boolean);
  if (named) return named;
  return vendorId || 'Vendor';
}

/** Staff invited list lives on rfq_payload — there is no vendor-invitations API. */
export function invitationsFromInvitedVendors(rfq) {
  return (rfq?.invited_vendor_ids || []).map((vendor_id) => ({
    vendor_id,
    state: 'INVITED',
  }));
}

export function invitationVendorId(inv) {
  if (!inv || typeof inv !== 'object') return '';
  return inv.vendor_id || inv.vendorId || inv.invited_vendor_id || '';
}

/** Staff-invited IDs plus anyone who registered or quoted via the public link. */
export function collectRfqVendorIds(rfq, invitations = [], quotes = []) {
  const seen = new Set();
  const ordered = [];
  const add = (id) => {
    const value = String(id || '').trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    ordered.push(value);
  };
  (rfq?.invited_vendor_ids || []).forEach(add);
  invitations.forEach((inv) => add(invitationVendorId(inv)));
  quotes.forEach((q) => add(q.vendor_id || q.vendorId));
  return ordered;
}

export function vendorParticipationSource(vendorId, rfq, invitation) {
  if ((rfq?.invited_vendor_ids || []).includes(vendorId)) return 'Invited';
  if (invitation?.vendor_name || invitation?.channel || invitation?.state) return 'Registered via link';
  return 'Quoted';
}
